import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Experiment from 'components/abTest/Experiment';
import {
  sizes, cagColors, baseColors, Button,
} from '@hyphengroup/cag-components';
import withApplicationConfig from 'containers/withApplicationConfig';
import { getRuntimeEnvVariables } from 'helpers/Utils';

const TermsConditions = styled.div`
  height: 30px;
  width: 100%;
  font-family: "ProximaNova";
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: 1.14;
`;

const SeeAllButton = styled.div`
  display: flex;
  height: 39px;
  font-weight: 300;
  font-size: 14px;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  border-top: 1px solid ${cagColors.all['bkground-01']};
  width: 100%;
  flex-grow: 1;
`;
const getDefaultFooter = ({
  dataSelectorId, onSeeMoreButtonClick, button, disclaimerSection,
}) => (
  <>
    <TermsConditions
      data-selector={dataSelectorId}
      data-selector-type="upperBottom"
      dangerouslySetInnerHTML={{ __html: disclaimerSection }}
    />
    <SeeAllButton
      data-selector={dataSelectorId}
      data-selector-type="lowerBottom"
    >
      <a
        href={button?.href}
        data-selector-type="link"
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: 'none' }}
      >
        <Button
          size="medium"
          variant="tertiary"
          base="classic"
          onClick={onSeeMoreButtonClick}
          minHeight={sizes(5)}
        >
          {button?.title}
        </Button>
      </a>
    </SeeAllButton>
  </>
);

const getExperimentFooter = ({
  variantId, dataSelectorId, onSeeMoreButtonClick, button, disclaimerSection,
}) => {
  const experimentVariables = [
    'RUNTIME_SS_WIDGET_TANDC_EXPERIMENT_VARIANT_1',
    'RUNTIME_SS_WIDGET_TANDC_EXPERIMENT_VARIANT_2',
    'RUNTIME_SS_WIDGET_TANDC_EXPERIMENT_VARIANT_3',
    'RUNTIME_SS_WIDGET_TANDC_EXPERIMENT_VARIANT_4',
    'RUNTIME_SS_WIDGET_TANDC_EXPERIMENT_VARIANT_5',
    'RUNTIME_SS_WIDGET_TANDC_EXPERIMENT_CONTROL',
  ];
  const [A, B, C, D, E, CONTROL] = experimentVariables.map(item => getRuntimeEnvVariables(item));
  switch (variantId) {
    case A:
    case B:
    case C:
      return (
        <>
          <SeeAllButton
            data-selector={dataSelectorId}
            data-selector-type="lowerBottom"
            style={variantId === B || variantId === C ? { backgroundColor: cagColors.all['bkground-01'] } : { backgroundColor: cagColors.all['brand-01'] }}
          >
            <a
              href={button?.href}
              data-selector-type="link"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <Button
                size="medium"
                variant={variantId === B ? 'secondary' : 'tertiary'}
                base="classic"
                minHeight={sizes(5)}
                onClick={onSeeMoreButtonClick}
              >
                {button?.title}
              </Button>
            </a>
          </SeeAllButton>
          <TermsConditions
            data-selector={dataSelectorId}
            data-selector-type="upperBottom"
            style={variantId === C ? {
              fontSize: '11px', backgroundColor: baseColors.oceanBlue, color: cagColors.all['brand-01'],
            } : { fontSize: '11px' }}
          >
            Terms and Conditions apply.
          </TermsConditions>
        </>
      );
    case D:
    case E:
      return (
        <>
          <TermsConditions
            data-selector={dataSelectorId}
            data-selector-type="upperBottom"
            style={variantId === E ? {
              fontSize: '11px',
              backgroundColor: cagColors.all['bkground-01'],
              borderTop: `2px solid ${cagColors.all['brand-01']}`,
            } : { fontSize: '11px', borderTop: `2px solid ${cagColors.all['brand-01']}` }}
          >
            Terms and Conditions apply.
          </TermsConditions>
          <SeeAllButton
            data-selector={dataSelectorId}
            data-selector-type="lowerBottom"
          >
            <a
              href={button?.href}
              data-selector-type="link"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: 'none',
                color: cagColors.all['bkground-01'],
              }}
            >
              <Button
                size="medium"
                variant="secondary"
                base="classic"
                minHeight={sizes(5)}
                onClick={onSeeMoreButtonClick}
              >
                {button?.title}
              </Button>
            </a>
          </SeeAllButton>
        </>
      );
    case CONTROL:
    default:
      return (
        <>
          <TermsConditions
            data-selector={dataSelectorId}
            data-selector-type="upperBottom"
            dangerouslySetInnerHTML={{ __html: disclaimerSection }}
          />
          <SeeAllButton
            data-selector={dataSelectorId}
            data-selector-type="lowerBottom"
          >
            <a
              href={button?.href}
              data-selector-type="link"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <Button
                size="medium"
                variant="tertiary"
                base="classic"
                onClick={onSeeMoreButtonClick}
                minHeight={sizes(5)}
              >
                {button?.title}
              </Button>
            </a>
          </SeeAllButton>
        </>
      );
  }
};

const Footer = (props) => (
  <Experiment
    experimentId={getRuntimeEnvVariables('RUNTIME_SS_WIDGET_TANDC_EXPERIMENT_ID')}
    controlVariantId={getRuntimeEnvVariables('RUNTIME_SS_WIDGET_TANDC_EXPERIMENT_CONTROL')}
    variantIds={(getRuntimeEnvVariables('RUNTIME_SS_WIDGET_TANDC_EXPERIMENT_VARIANTS') || '').split(',')}
  >
    {(variantId) => (
      props.experimentAffiliateWidgetList.includes(props.widgetTitle)
        ? getExperimentFooter({ ...props, variantId })
        : getDefaultFooter({ ...props })
    )}
  </Experiment>
);

Footer.propTypes = {
  experimentAffiliateWidgetList: PropTypes.arrayOf(PropTypes.string),
  widgetTitle: PropTypes.string,
  button: PropTypes.shape({
    href: PropTypes.string,
    title: PropTypes.string,
  }),
};

getExperimentFooter.propTypes = {
  variantId: PropTypes.string,
  dataSelectorId: PropTypes.string,
  onSeeMoreButtonClick: PropTypes.string,
  button: PropTypes.string,
  disclaimerSection: PropTypes.string,
};

getDefaultFooter.propTypes = {
  dataSelectorId: PropTypes.string,
  onSeeMoreButtonClick: PropTypes.string,
  button: PropTypes.string,
  disclaimerSection: PropTypes.string,
};

Footer.defaultProps = {
  experimentAffiliateWidgetList: [],
};

export default withApplicationConfig(Footer, ['routes', 'experimentAffiliateConfig']);
